import type { AegAuditLog } from "../api/aegOccurrences";
import type { StageKey } from "../components/aeg/types";

export type AegHistoryEntry = {
  key: string;
  user: string;
  createdAt: string;
  stage?: StageKey;
  // id de tradução (intl-msg:...) para eventos, ou o nome do campo da AEG para alterações de campo
  field: string;
  old: string;
  new: string;
};

const STATUS_TO_STAGE: Record<string, StageKey> = {
  REQUEST_OPENING: "abertura",
  PENDING_ANALYSIS: "pendente",
  PRE_APPROVAL: "pre",
  WARRANTY_ANALYSIS: "analise",
  CORRECT: "corrigir",
  REJECTED: "reprovado",
  CREDITED: "creditado",
};

export const toStageKey = (status: unknown): StageKey | undefined =>
  typeof status === "string" ? STATUS_TO_STAGE[status] : undefined;

const IGNORED_FIELDS = new Set(["id", "createdBy", "createdById", "updatedById", "createdAt", "updatedAt", "statusUpdatedAt"]);

const EVENT = {
  created: "intl-msg:support.aeg.detail.history.event.created",
  removed: "intl-msg:support.details.logOccurrenceRemoved",
  commentCreated: "intl-msg:support.details.logCommentCreated",
  commentEdited: "intl-msg:support.details.logCommentEdited",
  commentRemoved: "intl-msg:support.details.logCommentRemoved",
  fileAttached: "intl-msg:support.details.logFileAttached",
  fileRemoved: "intl-msg:support.details.logFileRemoved",
};

const isEmpty = (value: unknown) => value === null || value === undefined || value === "";
const asText = (value: unknown) => (isEmpty(value) ? "" : String(value));
const time = (log: AegAuditLog) => new Date(log.createdAt).getTime() || 0;

// Transforma os audit logs do back em linhas do histórico. Percorre do mais antigo ao mais novo para que
// comentários e anexos herdem a etapa em que a solicitação estava; o resultado sai do mais recente ao mais antigo.
export const buildAegHistoryEntries = (logs: AegAuditLog[]): AegHistoryEntry[] => {
  const entries: AegHistoryEntry[] = [];
  let stage: StageKey | undefined;

  [...logs]
    .sort((a, b) => time(a) - time(b) || a.id - b.id)
    .forEach((log) => {
      const base = { user: log.userName || "—", createdAt: log.createdAt };
      const push = (field: string, oldValue: unknown = "", newValue: unknown = "") =>
        entries.push({
          ...base,
          key: `${log.id}-${field}`,
          stage,
          field,
          old: asText(oldValue),
          new: asText(newValue),
        });

      if (log.entity === "AEG_OCCURRENCE") {
        if (log.action === "CREATE") {
          stage = toStageKey(log.newData?.status) ?? stage;
          push(EVENT.created);
        } else if (log.action === "DELETE") {
          push(EVENT.removed);
        } else {
          (log.data ?? []).forEach((item) => {
            if (IGNORED_FIELDS.has(item.input)) return;
            if (isEmpty(item.oldData) && isEmpty(item.newData)) return;
            if (item.input === "status") stage = toStageKey(item.newData) ?? stage;
            push(item.input, item.oldData, item.newData);
          });
        }
      } else if (log.entity === "AEG_OCCURRENCE_COMMENT") {
        if (log.action === "CREATE") push(EVENT.commentCreated, "", log.newData?.comment);
        else if (log.action === "DELETE") push(EVENT.commentRemoved);
        else {
          const change = (log.data ?? []).find((item) => item.input === "comment");
          if (change) push(EVENT.commentEdited, change.oldData, change.newData);
        }
      } else if (log.entity === "AEG_OCCURRENCE_FILE") {
        if (log.action === "CREATE") push(EVENT.fileAttached, "", log.newData?.name);
        else if (log.action === "DELETE") push(EVENT.fileRemoved, log.oldData?.name, "");
      }
    });

  return entries.reverse();
};

// campo da AEG -> chave de tradução já usada no formulário de nova solicitação
const FIELD = (name: string) => `intl-msg:support.aeg.newRequest.field.${name}`;
export const AEG_FIELD_LABELS: Record<string, string> = {
  status: "intl-msg:support.aeg.detail.history.field.status",
  aegNumber: "intl-msg:support.aeg.detail.history.field.aegNumber",
  requestType: FIELD("requestType"),
  approvalType: FIELD("approvalType"),
  responsible: FIELD("responsible"),
  dnResponsible: FIELD("responsibleDn"),
  dn: FIELD("dn"),
  regional: FIELD("regional"),
  phone: FIELD("phone"),
  chassis: FIELD("chassis"),
  licensePlate: FIELD("licensePlate"),
  vehicleType: FIELD("vehicleType"),
  customer: FIELD("customer"),
  implementOrBodyBuilder: FIELD("implement"),
  implementOrBodyBuilderOther: FIELD("implementOther"),
  bodyBuilderName: FIELD("bodyBuilder"),
  bodyBuilderNameOther: FIELD("bodyBuilderOther"),
  cargoTransport: FIELD("cargo"),
  odometer: FIELD("odometer"),
  hourMeter: FIELD("hourMeter"),
  repairDate: FIELD("repairDate"),
  osNumber: FIELD("serviceOrder"),
  sgNumber: FIELD("warrantyRequest"),
  serviceType: FIELD("serviceType"),
  repairTotal: FIELD("repairTotal"),
  laborTotalUt: FIELD("laborTotal"),
  attendanceCondition: FIELD("serviceCondition"),
  hasThirdPartyService: FIELD("hasThirdParty"),
  thirdPartyServices: FIELD("thirdPartyServices"),
  thirdPartyInvoiceAmount: FIELD("thirdPartyInvoiceValue"),
  thirdPartyInvoiceNumbers: FIELD("thirdPartyInvoiceNumber"),
  repairScope: FIELD("repairScope"),
  partialPercentage: FIELD("repairPercentage"),
  controlCode: FIELD("controlCode"),
  chameVolksNumber: FIELD("chameVolks"),
  breakdown: FIELD("breakdown"),
  fleetComposition: FIELD("fleetComposition"),
  lastVwcoManPurchase: FIELD("lastPurchase"),
  companies: FIELD("companies"),
  customerComplaint: FIELD("customerComplaint"),
  dnDiagnosis: FIELD("dnDiagnosis"),
  dnJustification: FIELD("dnJustification"),
  assignTo: FIELD("assignTo"),
};
